import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { AdminApi } from '../../services/api';
import type { EmailComplet } from '../../types';
import { formatDateHeure } from '../../shared/dates';

/**
 * Lien discret « Voir l'e-mail complet » sous un message venu d'une réponse
 * par e-mail : le fil n'en montre que le texte nouveau du client (historique
 * cité retiré côté serveur, inbound-mail/nettoyage-reponse.ts). Un clic
 * charge et déplie l'e-mail d'origine, un second le replie.
 */
export const LienEmailComplet: React.FC<{ ticketId: string; messageId: string; className?: string }> = ({
  ticketId, messageId, className = '',
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [email, setEmail] = useState<EmailComplet | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const basculer = async () => {
    if (ouvert) { setOuvert(false); return; }
    setOuvert(true);
    if (email || chargement) return;
    setChargement(true);
    setErreur(null);
    try {
      setEmail(await AdminApi.getEmailComplet(ticketId, messageId));
    } catch {
      setErreur("L'e-mail complet n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className={`mt-2 ${className}`}>
      <button
        type="button"
        onClick={basculer}
        aria-expanded={ouvert}
        className="inline-flex items-center gap-1 text-[11px] text-graphite/80 hover:text-encre underline underline-offset-2"
      >
        <Mail className="w-3 h-3" aria-hidden="true" />
        {ouvert ? "Masquer l'e-mail complet" : "Voir l'e-mail complet"}
      </button>
      {ouvert && (
        <div className="mt-2 rounded-lg border border-trait bg-white p-3 text-xs text-encre">
          {chargement && <p className="text-graphite">Chargement…</p>}
          {erreur && <p role="alert" className="text-red-600">{erreur}</p>}
          {email && (
            <>
              <p className="text-graphite">
                De : {email.de}
                {email.recuLe ? ` · reçu le ${formatDateHeure(email.recuLe)}` : ''}
              </p>
              {email.objet && <p className="text-graphite">Objet : {email.objet}</p>}
              <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words font-sans leading-relaxed">{email.texte}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LienEmailComplet;
